import type { EventInfo } from "./capture-store";

// ---------------------------------------------------------------------------
// API Response Types
// ---------------------------------------------------------------------------

export interface JoinResponse {
  participantId: string;
  token: string;
  displayName: string | null;
  email: string | null;
}

export interface GeoCheckResponse {
  allowed: boolean;
  distanceMeters: number;
}

export interface UploadRequestResponse {
  fileId: string;
  uploadUrl: string;
  uploadMethod: "put" | "multipart";
  chunkSize?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  public code?: string;
  public data?: Record<string, unknown>;

  constructor(
    public status: number,
    message: string,
    code?: string,
    data?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.data = data;
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    let code: string | undefined;
    let data: Record<string, unknown> | undefined;
    try {
      const body = await res.json() as Record<string, unknown>;
      if (typeof body.error === "string") message = body.error;
      if (typeof body.code === "string") code = body.code;
      // Capture any extra fields (title, startsAt, organizerName, etc.)
      const { error: _e, code: _c, ...rest } = body;
      if (Object.keys(rest).length > 0) data = rest;
    } catch {
      // Ignore parse errors
    }
    throw new ApiError(res.status, message, code, data);
  }
  return res.json() as Promise<T>;
}

function authHeaders(token: string): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export async function fetchEvent(slug: string): Promise<EventInfo> {
  const res = await fetch(`/api/event/${encodeURIComponent(slug)}`);
  return handleResponse<EventInfo>(res);
}

export async function joinEvent(
  slug: string,
  name?: string,
  email?: string,
): Promise<JoinResponse> {
  const res = await fetch(`/api/event/${encodeURIComponent(slug)}/join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email }),
  });
  return handleResponse<JoinResponse>(res);
}

export async function checkGeo(
  slug: string,
  token: string,
  lat: number,
  lng: number,
): Promise<GeoCheckResponse> {
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/check-geo`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ lat, lng }),
    },
  );
  return handleResponse<GeoCheckResponse>(res);
}

export async function requestUpload(
  slug: string,
  token: string,
  filename: string,
  contentType: string,
  sizeBytes: number,
): Promise<UploadRequestResponse> {
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/upload/request`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ filename, contentType, sizeBytes }),
    },
  );
  return handleResponse<UploadRequestResponse>(res);
}

export async function confirmUpload(
  slug: string,
  token: string,
  fileId: string,
): Promise<void> {
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/upload/confirm`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ fileId }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new ApiError(res.status, body.error ?? "Confirm upload failed");
  }
}

// ---------------------------------------------------------------------------
// Multipart Upload API
// ---------------------------------------------------------------------------

export async function createMultipartUpload(
  slug: string,
  token: string,
  fileId: string,
): Promise<{ uploadId: string }> {
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/upload/${fileId}/multipart/create`,
    {
      method: "POST",
      headers: authHeaders(token),
    },
  );
  return handleResponse<{ uploadId: string }>(res);
}

export async function uploadPart(
  slug: string,
  token: string,
  fileId: string,
  uploadId: string,
  partNumber: number,
  chunk: Blob,
): Promise<{ etag: string }> {
  const params = new URLSearchParams({
    uploadId,
    partNumber: String(partNumber),
  });
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/upload/${fileId}/multipart/part?${params}`,
    {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: chunk,
    },
  );
  return handleResponse<{ etag: string }>(res);
}

export async function completeMultipartUpload(
  slug: string,
  token: string,
  fileId: string,
  uploadId: string,
  parts: { partNumber: number; etag: string }[],
): Promise<void> {
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/upload/${fileId}/multipart/complete`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ uploadId, parts }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new ApiError(res.status, body.error ?? "Complete multipart failed");
  }
}

export async function submitEmail(
  slug: string,
  token: string,
  email: string,
): Promise<void> {
  const res = await fetch(
    `/api/event/${encodeURIComponent(slug)}/email`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ email }),
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new ApiError(res.status, body.error ?? "Submit email failed");
  }
}
